import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import FileUpload from '../components/FileUpload';
import FaqAccordion from '../components/FaqAccordion';
import ShareButtons from '../components/ShareButtons';
import { trackEvent } from '../utils/analytics';
import { downloadText } from '../utils/download';
import { Copy, ArrowRight, RotateCcw, ScanText, Check, XCircle, Download } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export default function ImageToText() {
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleExtract = async () => {
    if (!files.length) return toast.error('Please select an image');
    trackEvent('conversion_started', { tool_name: 'Image to Text (OCR)' });
    setStatus('processing'); setError(''); setResult(null);

    const formData = new FormData();
    formData.append('file', files[0]);

    try {
      const res = await axios.post('/api/convert/ocr', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setStatus('done');
      setResult(res.data);
      trackEvent('download_click', { tool_name: 'Image to Text (OCR)' });
      if (!res.data.text) {
        toast('No text detected in this image', { icon: '⚠️' });
      } else {
        toast.success('Text extracted!');
      }
    } catch (err) {
      setStatus('error');
      setError((err.response && err.response.data && err.response.data.error) || 'Text extraction failed');
    }
  };

  const handleReset = () => { setFiles([]); setStatus(null); setResult(null); setError(''); };

  const handleCopy = () => {
    navigator.clipboard.writeText(result.text).then(() => {
      toast.success('Copied to clipboard!');
      trackEvent('copy_text_click', { tool_name: 'Image to Text (OCR)' });
    }).catch(() => toast.error('Copy failed — select the text manually'));
  };

  return (
    <div>
      <Helmet>
        <title>Image to Text (OCR) — Extract Text from Images Free | ILoveConvert</title>
        <meta name="description" content="Free OCR tool — extract editable text from any JPG, PNG, or WebP image in seconds. No signup, no watermark, works on screenshots, scans, and photos." />
        <meta name="keywords" content="image to text, OCR, extract text from image, optical character recognition, jpg to text, png to text, screenshot to text, free ocr online" />
        <link rel="canonical" href="https://iloveconvert.in/image-to-text" />
        <meta property="og:title" content="Image to Text (OCR) — Extract Text from Images Free | ILoveConvert" />
        <meta property="og:description" content="Upload an image and get editable, copyable text in seconds. Free OCR, no signup, no watermark." />
        <meta property="og:url" content="https://iloveconvert.in/image-to-text" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Image to Text (OCR) — Extract Text from Images Free" />
        <meta name="twitter:description" content="Free OCR tool. Upload an image, get editable text instantly. No signup needed." />
      </Helmet>

      <section className="hero" style={{ padding: '40px 20px' }}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><ScanText size={44} strokeWidth={1.5} /></div>
        <h1 style={{ fontSize: 'clamp(1.6rem,4vw,2.4rem)', marginBottom: 8 }}>Image to Text (OCR)</h1>
        <p>Extract editable, copyable text from any image — free, no signup, no watermark</p>
      </section>

      <div style={{ maxWidth: 900, margin: '20px auto', padding: '0 20px' }}>
        <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text-muted)' }}>
          Upload a screenshot, scanned document, or photo containing text, and this tool reads it using optical
          character recognition (OCR) — the process of detecting individual characters in an image and converting
          them back into real, selectable text. It runs entirely on our own servers using an open-source OCR
          engine, not a third-party API. Works well on clear, reasonably high-contrast text — printed documents,
          screenshots, signs, and receipts. Handwriting and very low-resolution or blurry images are a genuinely
          harder problem for any OCR engine, so expect lower accuracy there.
        </p>
      </div>

      <div className="upload-page">
        {!status && (
          <>
            <FileUpload onFiles={setFiles} accept={{ 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'], 'image/webp': ['.webp'], 'image/bmp': ['.bmp'], 'image/tiff': ['.tif', '.tiff'] }} maxSize={25} />
            {files.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <button className="btn btn-primary btn-lg" onClick={handleExtract} style={{ width: '100%' }}>
                  <ArrowRight size={18} /> Extract Text
                </button>
              </div>
            )}
          </>
        )}

        {status === 'processing' && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div className="spinner" />
            <p style={{ marginTop: 20, fontWeight: 600 }}>Reading text from your image...</p>
            <p style={{ marginTop: 6, fontSize: '0.82rem', color: 'var(--text-muted)' }}>Usually takes a few seconds</p>
          </div>
        )}

        {status === 'done' && result && (
          <>
            <div className="result-box">
              <div className="check"><Check size={32} strokeWidth={3} /></div>
              <h3>{result.text ? 'Text Extracted!' : 'No Text Detected'}</h3>
              {result.text ? (
                <>
                  <textarea
                    readOnly
                    value={result.text}
                    style={{
                      width: '100%', minHeight: 180, marginTop: 14, padding: 14, borderRadius: 10,
                      border: '1px solid var(--border)', fontFamily: 'inherit', fontSize: '0.9rem',
                      lineHeight: 1.6, resize: 'vertical', boxSizing: 'border-box', background: 'var(--glass-2)',
                      color: 'var(--t-primary)'
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 14 }}>
                    <button onClick={handleCopy} className="download-btn" style={{ marginTop: 0 }}>
                      <Copy size={18} /> Copy Text
                    </button>
                    <button
                      onClick={() => downloadText(result.text, 'extracted-text.txt', 'Image to Text (OCR)')}
                      className="btn btn-outline"
                    >
                      <Download size={16} /> Download .txt
                    </button>
                  </div>
                  <ShareButtons toolLabel="Image to Text (OCR)" />
                </>
              ) : (
                <p style={{ marginTop: 10 }}>We couldn't find any readable text in this image. Try a clearer photo or a higher-resolution scan.</p>
              )}
            </div>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button className="btn btn-outline" onClick={handleReset}><RotateCcw size={16} /> Extract Another</button>
            </div>
          </>
        )}

        {status === 'error' && (
          <div style={{ background: '#fff0f0', border: '1px solid #fca5a5', borderRadius: 'var(--radius)', padding: 28, textAlign: 'center' }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: '#dc2626' }}><XCircle size={40} /></div>
            <h3 style={{ color: '#dc2626', fontFamily: 'Sora, sans-serif', marginBottom: 8 }}>Failed</h3>
            <p style={{ color: '#b91c1c', marginBottom: 20 }}>{error}</p>
            <button className="btn btn-primary" onClick={handleReset}><RotateCcw size={16} /> Try Again</button>
          </div>
        )}
      </div>

      <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 20px' }}>
        <h2>How image-to-text extraction works</h2>
        <ul>
          <li>Upload a JPG, PNG, WebP, BMP, or TIFF image containing text</li>
          <li>An OCR engine analyses the image and identifies individual characters based on their shapes</li>
          <li>Detected characters are reassembled into real, selectable text — not just an image of text anymore</li>
          <li>Copy the result directly, or download it as a plain .txt file</li>
        </ul>

        <h2>Where this is actually useful</h2>
        <ul>
          <li>Pulling text out of a screenshot instead of retyping it by hand</li>
          <li>Digitizing a scanned document, receipt, or printed page into editable text</li>
          <li>Extracting a quote, address, or phone number from a photo of a sign or business card</li>
          <li>Getting text out of a meme, slide, or image-based PDF page for quoting or searching</li>
        </ul>

        <h2>Getting the best result</h2>
        <ul>
          <li>Clear, high-contrast text (dark text on a light background) reads far more accurately than low-contrast or busy backgrounds</li>
          <li>Straight-on, unrotated, unblurred images work best — OCR engines are far more sensitive to skew and blur than the human eye</li>
          <li>Handwriting is a genuinely different, much harder problem than printed text — expect this to work best on printed or typed text</li>
          <li>Very low-resolution images (small screenshots stretched larger) lose the detail OCR needs — a higher-resolution source helps significantly</li>
        </ul>

        <div style={{ background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: '18px 22px', marginBottom: 32 }}>
          <strong>Want the full picture on OCR accuracy?</strong> Read <Link to="/articles/image-to-text-ocr-guide">Image to Text (OCR): How It Works and How to Get Accurate Results</Link> — including a real tested comparison of what actually improves results and what doesn't.
        </div>

        <h2>Frequently asked questions</h2>
        <FaqAccordion faqs={[
          { q: 'Is this tool free?', a: 'Yes, completely free with no signup required and no limit on how many images you process.' },
          { q: 'What image formats are supported?', a: 'Upload JPG, PNG, WebP, BMP, or TIFF. The extracted result is always plain text.' },
          { q: 'Does it work on handwriting?', a: 'It is built and tuned for printed or typed text. Handwriting recognition is a genuinely different, harder problem, so accuracy on handwritten text will be noticeably lower.' },
          { q: 'What languages does it support?', a: 'The current version is optimised for English text. Text in other languages or mixed scripts may not be recognised accurately.' },
          { q: 'Are my images kept private?', a: 'Yes. Text extraction runs on our own servers using a locally-run OCR engine — your image is never sent to a third-party service. The uploaded image is deleted from our servers immediately after processing, and we don\'t store the extracted text either.' },
        ]} />
      </div>
    </div>
  );
}
